import { useAppSelector } from "@/lib/redux/hooks"
import { Col, Empty, Row } from "antd"
import SearchingAirportItem from "./SearchingAirportItem"
import SearchingCityHeader from "./SearchingCityHeader"
import SearchingCountryHeader from "./SearchingCountryHeader"
import { Fragment, useContext, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { SelectPlacementContext } from "."
import { useAirports } from "@/hooks/flight/useAirports"
import { matcherTypes } from "@/lib/redux/features/flight/reduce"
import { TypeAirport } from "@/lib/redux/features/flight/type"
import {
  CountryCityNested,
  groupAirportsByCity,
  nestGroupsByCountry,
} from "@/utils/flight/groupAirportsByCity"

const SearchingResults = () => {
  const airportsData = useAirports()
  const countryCodes = useAppSelector(state => state.flight.countryCodes)
  const mappingCountriesAirports = useAppSelector(state => state.flight.mappingCountriesAirports)
  const searchPopular = useAppSelector(state => state.flight.searchPopular)
  const searchValue = useAppSelector(state => state.flight.searchValue)
  const { searching, onChange } = useContext(SelectPlacementContext)
  const { t } = useTranslation()

  const airports = useMemo(() => {
    const airportCodes = countryCodes.reduce((arr, countryCode) => {
      return arr.concat(mappingCountriesAirports[countryCode])
    }, [] as any[])

    return matcherTypes.reduce((airports, matcherType) => {
      return airports.sort((airport1: any, airport2: any) => {
        if (!!airport2.matchers && typeof airport2.matchers[matcherType] !== 'undefined') {
          return 1
        }
        return -1
      })
    }, airportCodes.map(code => airportsData[code]).filter(Boolean).sort((airport1: any, airport2: any) => {
      if (airport2.isPopular === '1') {
        return 1
      }
      return -1
    }))
  }, [countryCodes, mappingCountriesAirports, airportsData, searching])

  const { nestedByCountry, showCountryHeaders } = useMemo(() => {
    const sliced = airports.slice(0, 50) as (TypeAirport & {
      searchValue?: unknown
      matchers?: Record<string, unknown>
    })[]
    const cityGroups = groupAirportsByCity(sliced)
    const nested: CountryCityNested[] = nestGroupsByCountry(cityGroups)
    // Chỉ tách cấp quốc gia khi có >1 quốc gia trong kết quả — tránh lặp (vd. search "viet nam" + 1 nước)
    const showCountryHeaders = nested.length > 1
    return { nestedByCountry: nested, showCountryHeaders }
  }, [airports])

  if (!searching || searchPopular) {
    return <></>
  }

  if (!nestedByCountry.length) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={t("flight.airport_not_found", { query: searchValue })}
      />
    )
  }

  return (
    <Row>
      {nestedByCountry.map(countryBlock => (
        <Fragment key={countryBlock.countryCode}>
          {showCountryHeaders && (
            <Col span={24}>
              <SearchingCountryHeader countryName={countryBlock.country} />
            </Col>
          )}
          {countryBlock.cityGroups.map(cityGroup => {
            const multi = cityGroup.airports.length >= 2
            if (multi) {
              return (
                <Col
                  span={24}
                  key={cityGroup.cityKey}
                  className="home__searching--placement-city-group"
                >
                  <Row>
                    <Col span={24}>
                      <SearchingCityHeader airport={cityGroup.airports[0]} />
                    </Col>
                    {cityGroup.airports.map(airport => (
                      <Col span={24} key={airport.code}>
                        <SearchingAirportItem
                          layout="grouped"
                          onChange={onChange}
                          airport={airport}
                        />
                      </Col>
                    ))}
                  </Row>
                </Col>
              )
            }
            return (
              <Fragment key={cityGroup.cityKey}>
                {cityGroup.airports.map(airport => (
                  <Col span={24} key={airport.code}>
                    <SearchingAirportItem
                      layout="default"
                      onChange={onChange}
                      airport={airport}
                    />
                  </Col>
                ))}
              </Fragment>
            )
          })}
        </Fragment>
      ))}
    </Row>
  )
}

export default SearchingResults